/**
 * The email-template TYPE contract (D-settings-s3-email-templates, Phase 9 S3).
 *
 * The transactional template types are a BACKEND SES sender contract: each key maps
 * to a sender, and a new key needs backend work, so the admin EDITS this fixed set
 * (no create, no delete). The declared variables are REFERENCE DATA, a property of
 * the TYPE (identical for a key's uk and us rows), so they live HERE in one const,
 * NOT per row: the market rows cannot drift on which variables exist. The editor
 * looks up the declared set by templateKey and shows an "available variables" helper,
 * and validates that the authored copy references only declared variables.
 *
 * The real SES templates plus the HTML render and send are the migration seam; the
 * mock authors the template CONTENT (subject + body) and this variable contract.
 */

export interface EmailTemplateType {
  /** The fixed template key (the SES sender contract). */
  readonly key: string;
  /** Human label for the list and the form heading. */
  readonly label: string;
  /** The merge fields the backend supplies to this template (the sender contract). */
  readonly declaredVariables: readonly string[];
}

/** The fixed transactional template set (the legacy SES types). */
export const EMAIL_TEMPLATE_TYPES: readonly EmailTemplateType[] = [
  { key: 'welcome', label: 'Welcome', declaredVariables: ['firstName'] },
  {
    key: 'verify-email',
    label: 'Verify email',
    declaredVariables: ['firstName', 'verifyLink'],
  },
  {
    key: 'password-reset',
    label: 'Password reset',
    declaredVariables: ['firstName', 'resetLink'],
  },
  {
    key: 'mobile-otp',
    label: 'Mobile OTP',
    declaredVariables: ['firstName', 'otpCode'],
  },
  {
    key: 'cashback-added',
    label: 'Cashback added',
    declaredVariables: ['firstName', 'cashbackAmount', 'storeName'],
  },
  {
    key: 'cashout-requested',
    label: 'Cashout requested',
    declaredVariables: ['firstName', 'amount'],
  },
  {
    key: 'cashout-paid',
    label: 'Cashout paid',
    declaredVariables: ['firstName', 'amount', 'payoutMethod'],
  },
  {
    key: 'referral-earned',
    label: 'Referral earned',
    declaredVariables: ['firstName', 'referralAmount', 'friendName'],
  },
  {
    key: 'plus-activated',
    label: 'Plus activated',
    declaredVariables: ['firstName'],
  },
  {
    key: 'donation-thanks',
    label: 'Donation thanks',
    declaredVariables: ['firstName', 'charityName', 'amount'],
  },
];

/** Look up a template type by its key. */
export const templateTypeByKey = (key: string): EmailTemplateType | undefined =>
  EMAIL_TEMPLATE_TYPES.find((t) => t.key === key);

/** The declared variables for a template key (empty if the key is unknown). */
export const declaredVariablesFor = (key: string): readonly string[] =>
  templateTypeByKey(key)?.declaredVariables ?? [];

/** The display label for a template key (falls back to the key itself). */
export const templateLabel = (key: string): string =>
  templateTypeByKey(key)?.label ?? key;

/**
 * Every distinct `{{placeholder}}` name used in a piece of copy. The pattern is
 * `{{ name }}` with optional inner whitespace and a name of word characters.
 */
export const extractPlaceholders = (text: string): string[] => {
  const matches = text.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g);
  return [...new Set(Array.from(matches, (m) => m[1]))];
};

/**
 * The placeholders used across the given copy that are NOT declared for the template
 * key (the soft subset-validation, the page-schema Layer-2 shape). An undeclared
 * placeholder renders a literal broken token because the backend will not substitute
 * it. The caller WARNS naming these, but still allows the save (email templates have
 * no publish lifecycle, so the forgiving-save posture fits).
 */
export const undeclaredPlaceholders = (
  texts: readonly string[],
  key: string,
): string[] => {
  const declared = new Set(declaredVariablesFor(key));
  const used = new Set(texts.flatMap((t) => extractPlaceholders(t)));
  return [...used].filter((v) => !declared.has(v));
};
